// An end date is at most 30 days after the start, so a day-by-day view has at most 31 days to show.
export const MAX_SIMULATION_DAYS = 30;

// Plain milliseconds rather than calendar days, so the browser and the server agree on the limit in every time zone.
export const SIMULATION_DAY_MS = 24 * 60 * 60 * 1000;
const MAX_SIMULATION_MS = MAX_SIMULATION_DAYS * SIMULATION_DAY_MS;

/** The latest time a simulation that starts at `start` may end. */
export function getLatestSimulationEnd(start: Date): Date {
  return new Date(start.getTime() + MAX_SIMULATION_MS);
}

/** The latest end date a tester may choose: 30 days after the start, and never after `now`. */
export function getLatestAllowedEndDate(start: Date, now: Date): Date {
  return new Date(
    Math.min(getLatestSimulationEnd(start).getTime(), now.getTime()),
  );
}

// Stops at the end date, but never after `latest`. Without an end date, stops once every wait ends (`waitHorizon`), even after `latest`.
// Never before the start.
export function getSimulationWindowEnd({
  start,
  end,
  latest,
  waitHorizon,
}: {
  start: Date;
  end?: Date;
  latest: Date;
  waitHorizon?: Date;
}): Date {
  const windowEnd =
    end && !Number.isNaN(end.getTime())
      ? Math.min(end.getTime(), latest.getTime())
      : Math.max(latest.getTime(), waitHorizon?.getTime() ?? 0);
  // runJourney accepts a start slightly ahead of the server clock.
  return new Date(Math.max(start.getTime(), windowEnd));
}

// A day-by-day simulation stops at its end date, and never after `latest`, more than 30 days after the start, or before the start.
// runJourney fills in a blank end date, so every day's run gets the same window.
export function getDailySimulationWindowEnd({
  start,
  end,
  latest,
}: {
  start: Date;
  end?: Date;
  latest: Date;
}): Date {
  const latestEnd = getLatestAllowedEndDate(start, latest).getTime();
  const windowEnd =
    end && !Number.isNaN(end.getTime())
      ? Math.min(end.getTime(), latestEnd)
      : latestEnd;
  return new Date(Math.max(start.getTime(), windowEnd));
}

export function getSimulationLastDayIndex({
  start,
  windowEnd,
}: {
  start: Date;
  windowEnd: Date;
}): number {
  return Math.max(
    0,
    Math.ceil((windowEnd.getTime() - start.getTime()) / SIMULATION_DAY_MS),
  );
}
